import type { AuthResponse, JwtClaims, OrgInfo, ProfileInfo, Role, SessionUser } from '@getpay93/api';

export type RoleKind = 'business' | 'client';

export interface Session {
  token: string;
  user: SessionUser;
  org: OrgInfo | null;
  profiles: ProfileInfo[];
  role: Role;
  roleKind: RoleKind;
}

export function roleKindOf(role: Role | string | undefined): RoleKind {
  return role === 'client' ? 'client' : 'business';
}

export function hasOrg(s: Session | null): boolean {
  return !!s && !!s.org;
}

export function homePathFor(s: Session | null): string {
  if (!s) return '/login';
  if (!hasOrg(s)) return '/unirse';
  return '/app';
}

export function sessionFromAuth(auth: AuthResponse): Session {
  const role = auth.user.role;
  return {
    token: auth.accessToken,
    user: auth.user,
    org: auth.org ?? null,
    profiles: auth.profiles ?? [],
    role,
    roleKind: roleKindOf(role),
  };
}

/** Reconstruye sesión al boot: claims del JWT (user/role/orgId) + profiles frescos. */
export function sessionFromClaimsAndProfiles(
  token: string,
  claims: JwtClaims,
  profiles: ProfileInfo[],
): Session {
  const active =
    profiles.find((p) => p.active) ??
    profiles.find((p) => p.orgId === claims.orgId) ??
    profiles[0] ??
    null;
  const org: OrgInfo | null = active
    ? {
        id: active.orgId,
        name: active.orgName,
        type: active.orgType,
        country: active.country,
        currency: active.currency,
        timezone: active.timezone,
        logoUrl: active.logoUrl,
      }
    : null;
  const role = (claims.role as Role) ?? active?.role ?? 'client';
  return {
    token,
    user: {
      id: String(claims.userId ?? ''),
      email: String(claims.email ?? ''),
      name: String(claims.name ?? ''),
      role,
    },
    org,
    profiles,
    role,
    roleKind: roleKindOf(role),
  };
}
